import { useEffect, useState } from "react";
import type { WorkspacePrompt } from "@leaply-interview/shared-types";
import {
  type AdminLeaplyPlan,
  leaplyPlans,
  workspacePrompts,
} from "../lib/api";
import { ErrorBanner, formatApiError } from "./Studies";

/**
 * /prompts — Prompts hub (ported from `_prototype/app/(admin)/prompts/page.tsx`).
 *
 * Two sections:
 *
 *   Workspace      — composing fragments that load on every study (Company
 *                    knowledge / Conversation style / Interruption handling /
 *                    Boundaries / Wrap-up — the prototype's fixed schema).
 *                    Our backend is row-based (one row per fragment) and
 *                    strictly more flexible than the prototype's single
 *                    JSONB; we render whatever rows the admin has, sorted by
 *                    `sortOrder`, as autosave-on-blur cards. "+ New" sits at
 *                    the bottom — the typical workflow is editing the seed
 *                    five, not adding new ones.
 *
 *   Plan catalog   — one paragraph per Leaply plan. DB-backed (migration
 *                    1780099200000); editable here with the same
 *                    autosave-on-blur UX as the workspace prompts.
 *                    Per-participant plan eligibility (`availablePlanIds`)
 *                    comes from `identity.users` → snapshot at /initiate
 *                    and lives on `interviews.identity_snapshot` — that's
 *                    a different feed; this catalog is just the
 *                    agent-facing description Lea reads silently.
 *
 * Save UX: edit textarea inline → commit on blur (or ⌘/Ctrl-Enter). A small
 * indicator next to the title shows Saving… / ✓ Saved / Error. No "Save"
 * button — researchers shouldn't have to chase one per row.
 */

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

// Field-descriptor mapping — if a workspace_prompts row's title matches one
// of these labels (case-insensitive), use the friendlier hint copy below.
// Falls through to the row's own description otherwise.
const FIELD_HINTS: Record<string, string> = {
  "company knowledge":
    "What the agent needs to know about the company and product. Read silently — never recited.",
  "conversation style":
    "How the agent talks. One question at a time, reflective, non-leading. If the participant gave a sufficient answer briefly, move on.",
  "interruption handling":
    "What to do when the participant cuts the agent off mid-sentence.",
  boundaries:
    "Medical, identity, off-topic, support redirects, privacy. The agent's hard limits.",
  "wrap-up":
    "How the agent closes the call after the planned sections are covered.",
  "wrap up":
    "How the agent closes the call after the planned sections are covered.",
};

export function Prompts() {
  const [prompts, setPrompts] = useState<WorkspacePrompt[] | null>(null);
  const [plans, setPlans] = useState<AdminLeaplyPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refreshPrompts() {
    workspacePrompts
      .list()
      .then(({ prompts: rows }) =>
        setPrompts([...rows].sort((a, b) => a.sortOrder - b.sortOrder)),
      )
      .catch((err) => setError(formatApiError(err)));
  }

  useEffect(() => {
    refreshPrompts();
    leaplyPlans
      .list()
      .then(({ plans: rows }) => setPlans(rows))
      .catch((err) => setError(formatApiError(err)));
  }, []);

  function applyUpdated(updated: WorkspacePrompt) {
    setPrompts((prev) =>
      prev
        ? [...prev]
            .map((p) => (p.id === updated.id ? updated : p))
            .sort((a, b) => a.sortOrder - b.sortOrder)
        : prev,
    );
  }
  function applyRemoved(id: string) {
    setPrompts((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
  }
  function applyCreated(p: WorkspacePrompt) {
    setPrompts((prev) =>
      prev
        ? [...prev, p].sort((a, b) => a.sortOrder - b.sortOrder)
        : [p],
    );
  }

  return (
    <div className="min-h-full px-8 py-10 lg:px-14 lg:py-12 max-w-5xl">
      <h1 className="text-[34px] leading-[40px] font-semibold tracking-tight text-neutral-900 mb-2">
        Prompts
      </h1>
      <p className="text-[13px] text-neutral-500 leading-relaxed mb-10 max-w-prose">
        Edit the agent&apos;s background knowledge and rules of engagement once
        here, and every study in this workspace will use them. The per-study
        questions live on each study&apos;s Setup tab.
      </p>

      {error && <ErrorBanner message={error} />}

      {/* ── Workspace ──────────────────────────────────────────────────── */}
      <section className="mb-14">
        <div className="flex items-baseline gap-2 mb-1">
          <h2 className="text-[18px] font-semibold text-neutral-900">
            Workspace
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
            applies to every study
          </span>
        </div>
        <p className="text-[12px] text-neutral-500 leading-relaxed mb-5 max-w-prose">
          The agent reads these in order at the top of every conversation. Keep
          them concise — the longer they get, the slower the agent becomes at
          remembering the study-specific questions below.
        </p>

        {prompts === null && !error && (
          <p className="text-[13px] text-neutral-500">Loading…</p>
        )}

        {prompts !== null && prompts.length === 0 && (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-6 py-8 text-center">
            <p className="text-[13px] text-neutral-500 mb-3">
              No workspace prompts yet. Seed the five recommended fragments to
              get started.
            </p>
          </div>
        )}

        {prompts !== null && prompts.length > 0 && (
          <ul className="space-y-4">
            {prompts.map((p) => (
              <PromptRow
                key={p.id}
                prompt={p}
                onUpdated={applyUpdated}
                onRemoved={applyRemoved}
              />
            ))}
          </ul>
        )}

        {prompts !== null && (
          <NewPromptInline
            onCreated={applyCreated}
            currentMaxOrder={prompts.reduce(
              (m, p) => Math.max(m, p.sortOrder),
              0,
            )}
          />
        )}
      </section>

      {/* ── Plan catalog ───────────────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline gap-2 mb-1">
          <h2 className="text-[18px] font-semibold text-neutral-900">
            Plan catalog
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
            spliced in when a study selects this plan
          </span>
        </div>
        <p className="text-[12px] text-neutral-500 leading-relaxed mb-5 max-w-prose">
          One paragraph per Leaply plan. The agent reads the selected
          plan&apos;s paragraph silently — facts to recognize when the
          participant describes their plan, not to recite. Edits save on
          blur, same as the Workspace rows above.
        </p>

        {plans === null && !error && (
          <p className="text-[13px] text-neutral-500">Loading…</p>
        )}

        {plans !== null && (
          <ul className="space-y-4">
            {plans.map((p) => (
              <PlanRow
                key={p.id}
                plan={p}
                onUpdated={(next) =>
                  setPlans((prev) =>
                    prev
                      ? prev.map((x) => (x.id === next.id ? next : x))
                      : prev,
                  )
                }
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Per-prompt row ─────────────────────────────────────────────────────────

function PromptRow({
  prompt,
  onUpdated,
  onRemoved,
}: {
  prompt: WorkspacePrompt;
  onUpdated: (p: WorkspacePrompt) => void;
  onRemoved: (id: string) => void;
}) {
  const [title, setTitle] = useState(prompt.title);
  const [content, setContent] = useState(prompt.content);
  const [active, setActive] = useState(prompt.isActive);
  const [state, setState] = useState<SaveState>({ kind: "idle" });

  // Stay in sync when the parent reloads (e.g. after a sibling save).
  useEffect(() => setTitle(prompt.title), [prompt.title]);
  useEffect(() => setContent(prompt.content), [prompt.content]);
  useEffect(() => setActive(prompt.isActive), [prompt.isActive]);

  const dirty =
    title !== prompt.title ||
    content !== prompt.content ||
    active !== prompt.isActive;

  const hint =
    FIELD_HINTS[prompt.title.toLowerCase().trim()] ??
    prompt.description ??
    "";

  async function commit() {
    if (!dirty) return;
    setState({ kind: "saving" });
    try {
      const { prompt: updated } = await workspacePrompts.update(prompt.id, {
        title,
        content,
        isActive: active,
      });
      onUpdated(updated);
      setState({ kind: "saved" });
      setTimeout(
        () => setState((s) => (s.kind === "saved" ? { kind: "idle" } : s)),
        1500,
      );
    } catch (err) {
      setState({ kind: "error", message: formatApiError(err) });
    }
  }

  async function toggleActive() {
    const next = !active;
    setActive(next);
    setState({ kind: "saving" });
    try {
      const { prompt: updated } = await workspacePrompts.update(prompt.id, {
        isActive: next,
      });
      onUpdated(updated);
      setState({ kind: "saved" });
      setTimeout(
        () => setState((s) => (s.kind === "saved" ? { kind: "idle" } : s)),
        1200,
      );
    } catch (err) {
      setActive(!next);
      setState({ kind: "error", message: formatApiError(err) });
    }
  }

  async function remove() {
    if (!window.confirm(`Delete "${prompt.title}"?`)) return;
    try {
      await workspacePrompts.remove(prompt.id);
      onRemoved(prompt.id);
    } catch (err) {
      setState({ kind: "error", message: formatApiError(err) });
    }
  }

  return (
    <li
      className={`rounded-lg border border-neutral-200 bg-white px-5 py-4 ${
        active ? "" : "opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => void commit()}
          className="text-[15px] font-semibold text-neutral-900 bg-transparent border-0 focus:outline-none focus:ring-0 px-0 py-0 flex-1 min-w-0"
        />
        <div className="flex items-center gap-2 shrink-0">
          <SaveIndicator state={state} dirty={dirty} />
          <button
            type="button"
            onClick={() => void toggleActive()}
            className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${
              active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-neutral-100 text-neutral-500 border-neutral-200"
            }`}
            title={
              active ? "Active — included in study prompts" : "Inactive — skipped"
            }
          >
            {active ? "Active" : "Inactive"}
          </button>
          <button
            type="button"
            onClick={() => void remove()}
            aria-label="Delete prompt"
            className="text-neutral-400 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
              <path
                d="M3 4h10M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5M4 4l1 10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {hint && (
        <p className="text-[12px] text-neutral-500 leading-relaxed mb-3">
          {hint}
        </p>
      )}

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        rows={Math.min(14, Math.max(4, content.split("\n").length))}
        placeholder={`${prompt.title}…`}
        className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2.5 text-[12px] font-mono leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100 transition resize-y"
      />
    </li>
  );
}

// ── Inline "+ New prompt" row ──────────────────────────────────────────────

function NewPromptInline({
  onCreated,
  currentMaxOrder,
}: {
  onCreated: (p: WorkspacePrompt) => void;
  currentMaxOrder: number;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { prompt } = await workspacePrompts.create({
        title: title.trim(),
        content: content.trim(),
        isActive: true,
      });
      // Server doesn't auto-place at the end — patch the sortOrder so it
      // lands after the current last row (otherwise it defaults to 0 and
      // shows at the top).
      const placed =
        prompt.sortOrder === 0 && currentMaxOrder > 0
          ? (
              await workspacePrompts.update(prompt.id, {
                sortOrder: currentMaxOrder + 1,
              })
            ).prompt
          : prompt;
      onCreated(placed);
      setTitle("");
      setContent("");
      setOpen(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-neutral-500 hover:text-neutral-900 transition-colors"
      >
        <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
          <path
            d="M8 3v10M3 8h10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        New prompt fragment
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-dashed border-neutral-300 bg-white px-5 py-4 space-y-3">
      <h3 className="text-[13px] font-semibold text-neutral-900">
        New prompt fragment
      </h3>
      {error && <ErrorBanner message={error} />}
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (e.g. Boundaries)"
        autoFocus
        className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-[14px] focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Content…"
        rows={6}
        className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-[12px] font-mono leading-relaxed focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100 resize-y"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving || !title.trim() || !content.trim()}
          className="rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-300 px-3 py-1.5 text-[12px] font-medium text-white transition-colors"
        >
          {saving ? "Creating…" : "Create"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setTitle("");
            setContent("");
            setError(null);
          }}
          className="text-[12px] text-neutral-500 hover:text-neutral-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Save indicator ─────────────────────────────────────────────────────────

function SaveIndicator({
  state,
  dirty,
}: {
  state: SaveState;
  dirty: boolean;
}) {
  return (
    <div className="text-[11px] text-neutral-500 min-h-[16px] min-w-[80px] text-right">
      {state.kind === "saved" && (
        <span className="text-emerald-700">✓ Saved</span>
      )}
      {state.kind === "saving" && <span>Saving…</span>}
      {state.kind === "error" && (
        <span className="text-red-700" title={state.message}>
          Error
        </span>
      )}
      {state.kind === "idle" && dirty && <span>Unsaved — blur to save</span>}
    </div>
  );
}

// ── Plan catalog row ──────────────────────────────────────────────────────
// Autosave-on-blur editable description card. Same UX shape as PromptRow
// above — name is read-only here because the slug is the id studies
// reference, and renaming the display name without changing semantics is
// rare enough to not justify the inline-edit handle (use a migration
// instead).

function PlanRow({
  plan,
  onUpdated,
}: {
  plan: AdminLeaplyPlan;
  onUpdated: (p: AdminLeaplyPlan) => void;
}) {
  const [description, setDescription] = useState(plan.description);
  const [state, setState] = useState<SaveState>({ kind: "idle" });

  useEffect(() => setDescription(plan.description), [plan.description]);

  const dirty = description !== plan.description;

  async function commit() {
    if (!dirty) return;
    setState({ kind: "saving" });
    try {
      const { plan: updated } = await leaplyPlans.update(plan.id, {
        description,
      });
      onUpdated(updated);
      setState({ kind: "saved" });
      setTimeout(
        () => setState((s) => (s.kind === "saved" ? { kind: "idle" } : s)),
        1500,
      );
    } catch (err) {
      setState({ kind: "error", message: formatApiError(err) });
    }
  }

  return (
    <li className="rounded-lg border border-neutral-200 bg-white px-5 py-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-[15px] font-semibold text-neutral-900">
          {plan.name}
          <code className="ml-2 text-[10px] font-mono font-normal text-neutral-400 uppercase tracking-wider">
            {plan.id}
          </code>
        </h3>
        <SaveIndicator state={state} dirty={dirty} />
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        rows={Math.min(8, Math.max(3, description.split("\n").length + 1))}
        placeholder="Describe this plan for the agent…"
        className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2.5 text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100 transition resize-y"
      />
    </li>
  );
}
